import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  FolderKanban, Plus, ChevronRight, Loader2, CheckCircle2, Clock, MapPin, FileText, Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectsPage = () => {
  const { user, isCompany } = useAuth();
  const [projects, setProjects] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = filter !== 'all' ? `?status=${filter}` : '';
        const [projRes, jobsRes] = await Promise.all([
          axios.get(`${API}/projects${params}`, { withCredentials: true }),
          isCompany ? axios.get(`${API}/my-jobs`, { withCredentials: true }) : Promise.resolve({ data: [] })
        ]);
        setProjects(projRes.data || []);
        setMyJobs(jobsRes.data || []);
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filter, isCompany]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="projects-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="projects-title">
              Mes Projets
            </h1>
            <p className="text-[#4B5563] mt-1">Suivez l'avancement de vos chantiers</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['all', 'active', 'completed'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-[#1E3A8A]' : ''}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'Tous' : f === 'active' ? 'En cours' : 'Terminés'}
            </Button>
          ))}
        </div>

        {/* My Published Jobs */}
        {isCompany && myJobs.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-[#111827] mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#1E3A8A]" />
              Mes chantiers publiés
            </h2>
            <div className="space-y-3">
              {myJobs.map((job) => (
                <Link to={`/jobs/${job.id}`} key={job.id}>
                  <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30 transition-colors mb-3" data-testid={`published-job-${job.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-[#111827]">{job.title}</h3>
                            <Badge className={job.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                              {job.status === 'open' ? 'Ouvert' : 'Fermé'}
                            </Badge>
                            {job.is_urgent && <Badge className="bg-red-100 text-red-700">Urgent</Badge>}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-[#6B7280]">
                            {job.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{job.city}</span>}
                            <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{job.applications_count || 0} candidature(s)</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#6B7280] ml-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Projects (accepted applications) */}
        {projects.length > 0 && (
          <h2 className="font-heading font-semibold text-xl text-[#111827] mb-4 flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-[#1E3A8A]" />
            Projets en cours
          </h2>
        )}

        {projects.length > 0 ? (
          <div className="space-y-4">
            {projects.map((project) => (
              <Link to={`/dashboard/projects/${project.id}`} key={project.id}>
                <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30 transition-colors mb-4" data-testid={`project-card-${project.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-heading font-semibold text-lg text-[#111827]">
                            {project.job_title}
                          </h3>
                          <Badge className={project.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                            {project.status === 'completed' ? 'Terminé' : 'En cours'}
                          </Badge>
                        </div>
                        <p className="text-sm text-[#4B5563] mb-3">
                          {isCompany ? `Sous-traitant: ${project.contractor_name}` : `Client: ${project.company_name}`}
                        </p>
                        
                        {/* Progress */}
                        <div className="flex items-center gap-4">
                          <div className="flex-1 max-w-xs">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-[#1E3A8A] h-2 rounded-full transition-all"
                                style={{ width: `${project.progress}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-sm font-medium text-[#374151]">{project.progress}%</span>
                          <span className="text-sm text-[#6B7280]">
                            {project.milestones?.filter(m => m.status === 'approved').length || 0}/{project.milestones?.length || 0} jalons
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#6B7280] ml-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <FolderKanban className="w-12 h-12 mx-auto text-[#6B7280]/40 mb-4" />
              <p className="text-[#4B5563] mb-2">Aucun projet en cours</p>
              <p className="text-sm text-[#6B7280]">
                {isCompany
                  ? 'Créez un projet en acceptant une candidature sur un de vos chantiers'
                  : 'Les projets apparaîtront ici quand un donneur d\'ordre vous en assignera un'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
